import React, { useContext } from 'react';

import { AngularContext } from 'react-app';
import t from 'react-translate';

import { Course } from 'redux/schemas/models/course';

type EnrollmentTableCellDataProps = {
  course: Course;
  // Highlight the text in primary color even when there's no link.
  // Used by the institutions dashboard, where the hover popover already
  // styles the number as interactive regardless of admin permissions.
  highlightWhenNotLinked?: boolean;
};

const EnrollmentTableCellData = ({
  course,
  highlightWhenNotLinked = false,
}: EnrollmentTableCellDataProps) => {
  const { injectServices } = useContext(AngularContext);
  const [$state, CurrentPermissionsManager] = injectServices([
    '$state',
    'CurrentPermissionsManager',
  ]);

  const hasCourseAdminPermissions = CurrentPermissionsManager.hasOrgAdminPermissions()
    || CurrentPermissionsManager.hasCourseManagerPermissions();
  const numEnrolled = course.numEnrolled ?? 0;
  const canAccessUserManagement = hasCourseAdminPermissions && numEnrolled > 0;

  const hasLimit = course.enrollmentLimit !== null && course.enrollmentLimit > 0;
  const enrollmentText = hasLimit
    ? `${numEnrolled}/${course.enrollmentLimit}`
    : numEnrolled;

  const showWaitlist = hasLimit
    && course.enrollmentLimit <= numEnrolled
    && course.enrollmentWaitlistEnabled
    && !!course.numWaitlistEntries;

  const isHighlighted = canAccessUserManagement || (highlightWhenNotLinked && numEnrolled > 0);
  const textClasses = `num-enrollees-text font-weight-bold ${isHighlighted ? 'text-primary' : ''}`;

  const content = (
    <div className='num-enrollees-container'>
      <span className={textClasses}>{enrollmentText}</span>
      {showWaitlist && (
        <div className={textClasses}>
          {t.COURSE_ADMIN_DASHBOARD.WAITLISTED(course.numWaitlistEntries)}
        </div>
      )}
    </div>
  );

  if (!canAccessUserManagement) {
    return content;
  }

  const userMgmtUrl = $state.href(`${course.isJourney ? 'learning-journey-user-management-learners' : 'user-management-learners'}`, {
    catalogId: course.catalogId,
  });

  return (
    <a className='text-primary' href={userMgmtUrl}>
      {content}
    </a>
  );
};

export default EnrollmentTableCellData;
